/**
 * Prompt Review Draft page (PROC-75).
 *
 * Route: ``/prompts/:key/review-draft``.
 *
 * The finalization step between iterating on a draft (on the Evaluations
 * page) and opening a real PR. Reads the current draft + yaml source and
 * renders a per-field diff so the BCBA can see exactly what will land in
 * the pipelines yaml + api-django registry mirror. "Open PR" here calls
 * the same endpoint the old footer button used, but only after this
 * explicit review step.
 *
 * If the user opens this page with no draft state, we show an empty-state
 * pointing them back to the evaluations page — the page always assumes
 * you got here intentionally, so we don't auto-redirect (which would be
 * confusing back-button behavior).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
// `diff` (jsdiff) is imported as a transitive dep — several devtools
// deps pull it. If we start depending on this more heavily, promote it
// to a direct dep in package.json so we own the version pin.
import { diffLines } from "diff";
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { Button, Heading, Label, Paragraph } from "@fronterahealth/frontera-ui-components";

import {
  getPromptDraft as getPromptDraftRaw,
  useCreatePromptWithPr,
  useDeletePromptDraft,
  useEditPromptYamlAndOpenPr,
  useGetPromptDetails,
  useListAvailableRoles,
  useListAvailableTechnicalInstructions,
  useListAvailableTemplatedInstructions,
  useListPromptYamlFiles,
} from "@api/rest/types-and-hooks";
import type { CreatePromptWithPRInput, GetPromptDraftOutput as PromptDraft } from "@api/rest/types-and-hooks";
import { Breadcrumb } from "@components/Breadcrumb/Breadcrumb";
import { notifyError } from "@components/notifications/notifications";
import { PROpenedDialog } from "@pages/Prompts/PROpenedDialog";
import type { DraftOverrides } from "@pages/Prompts/promptDraftFields";
import { scrubLiveOnlyKeys } from "@pages/Prompts/promptDraftFields";
import { computeChanges } from "@pages/Prompts/promptReviewDiff";

async function unwrap<T>(resp: { data: T | { detail?: string }; status: number }): Promise<T> {
  if (resp.status !== 200) {
    const detail = (resp.data as { detail?: string })?.detail ?? `HTTP ${resp.status}`;
    throw new Error(detail);
  }
  return resp.data as T;
}
async function getPromptDraft(key: string): Promise<PromptDraft> {
  return unwrap(await getPromptDraftRaw(key));
}

/** Query key must match PromptTestBed's `draftQueryKey`. */
function draftQueryKey(key: string) {
  return ["prompt-draft", key] as const;
}

interface DiffCardProps {
  label: string;
  before: string;
  after: string;
  format?: "text" | "json";
}

interface DiffLineRow {
  key: string;
  line: string;
  kind: "added" | "removed" | "context";
}

/** Compute a line-level unified diff. Splits each `diffLines` chunk into
 *  actual rows so we can render each line with its own prefix + colour
 *  (matches GitHub PR-diff feel). Trailing empty lines from `.split("\n")`
 *  get dropped since they're an artifact of the split, not content. */
function computeDiffRows(before: string, after: string): DiffLineRow[] {
  const changes = diffLines(before || "", after || "");
  const rows: DiffLineRow[] = [];
  changes.forEach((change, ci) => {
    const raw = change.value.split("\n");
    if (raw.length > 0 && raw[raw.length - 1] === "") raw.pop();
    raw.forEach((line, li) => {
      rows.push({
        key: `${ci}-${li}`,
        line,
        kind: change.added ? "added" : change.removed ? "removed" : "context",
      });
    });
  });
  return rows;
}

/** One diff card per changed field. Unified line-level diff: unchanged
 *  lines rendered as context (grey), removals in red, additions in green
 *  — the shape most engineers already read on GitHub. */
function DiffCard({ label, before, after, format = "text" }: DiffCardProps) {
  const beforeText = format === "json" ? tryFormatJson(before) : before;
  const afterText = format === "json" ? tryFormatJson(after) : after;
  const rows = useMemo(() => computeDiffRows(beforeText, afterText), [beforeText, afterText]);

  return (
    <div className="flex flex-col gap-2 p-4 rounded-xl border border-border-medium bg-bg-surface">
      <div className="flex items-center gap-2">
        <Label size="small" colorType="secondary" className="uppercase tracking-wide">
          {label}
        </Label>
        <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-medium">changed</span>
      </div>
      <div className="rounded border border-border-subtle bg-bg-default text-xs font-mono max-h-96 overflow-y-auto">
        {rows.length === 0 ? (
          <div className="p-2 italic text-text-tertiary">(no content)</div>
        ) : (
          rows.map(({ key, line, kind }) => {
            const cls =
              kind === "added"
                ? "bg-emerald-50 text-emerald-900"
                : kind === "removed"
                  ? "bg-red-50 text-red-900"
                  : "text-text-secondary";
            const prefix = kind === "added" ? "+" : kind === "removed" ? "-" : " ";
            return (
              <div key={key} className={`${cls} flex whitespace-pre-wrap break-words px-2`}>
                <span className="select-none pr-2 opacity-60 shrink-0">{prefix}</span>
                <span className="flex-1">{line || " "}</span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function tryFormatJson(raw: string): string {
  if (!raw) return raw;
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

export const PromptReviewDraft: React.FC = () => {
  const { key } = useParams<{ key: string }>();
  const navigate = useNavigate();

  const {
    data: draft,
    isLoading: draftLoading,
    isError: draftIsError,
    error: draftError,
  } = useQuery({
    queryKey: draftQueryKey(key ?? ""),
    queryFn: () => getPromptDraft(key ?? ""),
    enabled: !!key,
  });

  const {
    data: detailsResp,
    isLoading: detailsLoading,
    isError: detailsIsError,
  } = useGetPromptDetails(key ?? "", {
    query: { enabled: !!key },
  });
  const prompt = detailsResp?.status === 200 ? detailsResp.data : null;
  // Distinguish "details failed to load" from "prompt has no diff." Without
  // this, a non-200 details response left ``prompt = null`` → no changes
  // → the empty "Nothing to review" state, even when a saved draft
  // exists. Match the evaluations page's explicit error handling.
  const detailsErrorMessage = useMemo(() => {
    if (detailsIsError) return "Failed to load prompt details.";
    if (detailsResp && detailsResp.status !== 200) {
      const detail = (detailsResp.data as { detail?: string } | undefined)?.detail;
      return detail ?? `Failed to load prompt details (status ${detailsResp.status}).`;
    }
    return null;
  }, [detailsIsError, detailsResp]);

  // Same treatment for the draft fetch — a failed request must not fall
  // through to "Nothing to review," which reads as "no draft exists"
  // when the truth is "we don't know because the fetch failed." Match
  // the evaluations page's explicit error surface.
  const draftErrorMessage = useMemo(() => {
    if (!draftIsError) return null;
    return draftError instanceof Error ? draftError.message : "Failed to load prompt draft.";
  }, [draftIsError, draftError]);

  // Catalogs for resolving role/templated/technical keys back to text.
  const { data: rolesResp, isLoading: rolesLoading } = useListAvailableRoles();
  const { data: templatedResp, isLoading: templatedLoading } = useListAvailableTemplatedInstructions();
  const { data: technicalResp, isLoading: technicalLoading } = useListAvailableTechnicalInstructions();
  const rolesCatalog = rolesResp?.status === 200 ? rolesResp.data.items : undefined;
  const templatedCatalog = templatedResp?.status === 200 ? templatedResp.data.items : undefined;
  const technicalCatalog = technicalResp?.status === 200 ? technicalResp.data.items : undefined;

  const overrides = (draft?.draft_overrides ?? {}) as DraftOverrides;
  // If a keyed-lookup field (role / templated / technical) is in the
  // draft but its catalog hasn't resolved yet, ``resolveKeyOrLiteral``
  // falls back to the raw key text — which then diffs against the fully
  // resolved yaml text as if it were the new value. That would flash a
  // huge false diff while the catalog is in flight. Wait for the
  // catalogs the draft actually depends on before computing changes.
  const needsRoles = "role" in overrides;
  const needsTemplated = "templated_instructions" in overrides;
  const needsTechnical = "technical_instructions" in overrides;
  const catalogsPending =
    (needsRoles && rolesLoading) || (needsTemplated && templatedLoading) || (needsTechnical && technicalLoading);
  const changes = useMemo(
    () =>
      prompt && !catalogsPending
        ? computeChanges(prompt, overrides, rolesCatalog, templatedCatalog, technicalCatalog)
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- overrides derived from draft
    [prompt, draft, rolesCatalog, templatedCatalog, technicalCatalog, catalogsPending],
  );

  const openPRMutation = useEditPromptYamlAndOpenPr();
  const createPRMutation = useCreatePromptWithPr();
  const deleteDraftMutation = useDeletePromptDraft();
  const queryClient = useQueryClient();

  // ``registry_status === "missing"`` means the Prompt DB row exists
  // (created up front by the ``New Prompt`` flow) but no yaml file
  // declares it yet. In that case the ``Open PR`` action must call
  // ``CreatePromptWithPr`` (adds a fresh yaml entry + opens paired PRs)
  // rather than ``EditPromptYamlAndOpenPr`` (which requires an existing
  // yaml owner and would 404). The user also has to pick a target yaml
  // file, so we surface a dropdown at the bottom of the diff.
  const isNewPrompt = prompt?.registry_status === "missing";
  const [targetYamlFilename, setTargetYamlFilename] = useState<string>("");
  const { data: yamlFilesResp } = useListPromptYamlFiles({ query: { enabled: isNewPrompt } });
  const yamlFilenames: string[] = yamlFilesResp?.status === 200 ? yamlFilesResp.data.filenames : [];

  // Distinguishes "no draft saved at all" from "draft exists but every
  // field matches yaml." The evaluations footer opens Review as soon as
  // any override is saved, so a save-then-revert flow can strand a
  // no-op draft that's invisible from here. When that happens we still
  // show the empty state, but also offer a one-click clear so the user
  // isn't stuck with a persistent-but-invisible draft.
  const draftHasOverrides = Object.keys(overrides).length > 0;
  const draftIsNoOp = draftHasOverrides && changes.length === 0;

  // URLs get set after a successful mutation; the `PROpenedDialog`
  // renders when both are non-null. Kept in local state (not driven by
  // mutation.data) so the dialog stays open until the user explicitly
  // dismisses it.
  const [openedUrls, setOpenedUrls] = useState<{ pipelines: string; apiDjango: string } | null>(null);

  const handleOpenPR = useMutation({
    mutationFn: async () => {
      if (!key) throw new Error("no prompt key");
      if (!prompt) throw new Error("Prompt details haven't loaded yet.");
      if (Object.keys(overrides).length === 0) {
        throw new Error("Draft is empty — nothing to promote.");
      }

      // New-prompt path: yaml doesn't own this key yet, so open a
      // ``create-with-pr`` PR instead of an ``edit`` PR. Requires the
      // full field set (instructions is mandatory) plus a target yaml
      // file to write into.
      if (isNewPrompt) {
        if (!targetYamlFilename) {
          throw new Error("Pick a target yaml file before opening the PR.");
        }
        if (!overrides.instructions || !String(overrides.instructions).trim()) {
          throw new Error(
            "Instructions are required for a new prompt. Head back to the evaluations page to fill them in.",
          );
        }
        const payload: CreatePromptWithPRInput = {
          prompt_key: key,
          name: prompt.name,
          target_yaml_filename: targetYamlFilename,
          instructions: String(overrides.instructions),
          role: (overrides.role as string | null | undefined) ?? null,
          templated_instructions: (overrides.templated_instructions as string | null | undefined) ?? null,
          technical_instructions: (overrides.technical_instructions as string | null | undefined) ?? null,
          inputs: overrides.inputs ?? [],
          // sample_output/response_schema are DB-only and set via the
          // Evaluations page's Save button after creation, not here
          // (PROC-190) -- see scrubLiveOnlyKeys's docstring for why.
          // ``organization`` on the details payload is a nested object
          // whose ``id`` is the org PK we want on the PR. Global prompts
          // have ``organization: null`` — pass through as undefined so
          // the backend treats it as org-less.
          organization_id: prompt.organization?.id ?? undefined,
        };
        const resp = await createPRMutation.mutateAsync({ data: payload });
        if (resp.status !== 201) {
          const detail = (resp.data as { detail?: string } | undefined)?.detail;
          throw new Error(detail ?? `Failed to open PR (status ${resp.status}).`);
        }
        return resp.data;
      }

      // Edit path — existing prompt in yaml, only the changed fields go
      // through the edit endpoint. Scrub response_schema/sample_output
      // even though this page no longer displays them: a prompt whose
      // draft_overrides still carries one from before PROC-190 must not
      // have it silently re-shipped here, potentially clobbering a
      // newer value set through the Evaluations page's direct write.
      const payload: Record<string, unknown> = scrubLiveOnlyKeys(overrides as Record<string, unknown>);
      const resp = await openPRMutation.mutateAsync({ key, data: payload });
      if (resp.status !== 201) {
        const detail = (resp.data as { detail?: string } | undefined)?.detail;
        throw new Error(detail ?? `Failed to open PR (status ${resp.status}).`);
      }
      // Keep the draft on the Prompt row after opening the PR so the
      // evaluations page keeps showing the user's edits until the
      // pipelines PR merges. Duplicate-PR prevention is handled by
      // the button's disabled=(openedUrls != null) guard.
      return resp.data;
    },
    onSuccess: (data) => {
      setOpenedUrls({ pipelines: data.pipelines_pr_url, apiDjango: data.api_django_pr_url });
    },
    onError: (err) => {
      notifyError(err instanceof Error ? err.message : "Failed to open PR.");
    },
  });

  if (!key) return null;
  if (draftLoading || detailsLoading || catalogsPending) {
    return (
      <div className="flex flex-col bg-bg-default min-h-full px-4 sm:px-8 lg:px-16 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-64 rounded bg-limestone-200" />
          <div className="h-24 w-full rounded-xl bg-limestone-100" />
          <div className="h-24 w-full rounded-xl bg-limestone-100" />
        </div>
      </div>
    );
  }

  const noDraft = changes.length === 0;

  return (
    <div className="flex flex-col bg-bg-default min-h-full px-4 sm:px-8 lg:px-16 py-8">
      <Breadcrumb
        crumbs={[
          { label: "Prompts", to: "/prompts" },
          { label: key, to: `/prompts/${key}` },
          { label: "Evaluations", to: `/prompts/${key}/evaluations` },
          { label: "Review draft" },
        ]}
      />
      <div className="flex items-baseline gap-3 mb-2 flex-wrap">
        <Heading size="medium">Review draft</Heading>
        {prompt?.name && <code className="text-sm font-mono text-text-secondary">{prompt.name}</code>}
      </div>
      <Paragraph size="small" colorType="secondary" className="mb-6 max-w-2xl">
        Confirms what will land in the pipelines yaml + api-django registry when you open the PR. Fields not shown below
        match the yaml source and won&apos;t be included in the diff.
      </Paragraph>

      {draftErrorMessage || detailsErrorMessage ? (
        // Surface either fetch failure explicitly. Without this the
        // page silently falls through to the "Nothing to review" empty
        // state (since ``prompt`` is null → no diff), which reads as
        // "no draft" when the truth is "we don't know."
        <div className="flex flex-col items-center gap-2 py-16 rounded-xl border border-border-medium bg-bg-surface text-center">
          <Heading size="small">{draftErrorMessage ? "Couldn't load draft" : "Couldn't load prompt details"}</Heading>
          <Paragraph colorType="secondary" className="max-w-lg">
            {draftErrorMessage || detailsErrorMessage}
          </Paragraph>
          <Link
            to={`/prompts/${encodeURIComponent(key)}/evaluations`}
            className="text-sm underline text-text-primary hover:text-text-secondary"
          >
            Back to evaluations
          </Link>
        </div>
      ) : noDraft ? (
        <div className="flex flex-col items-center gap-3 py-16 rounded-xl border border-border-medium bg-bg-surface text-center">
          <Heading size="small">Nothing to review</Heading>
          <Paragraph colorType="secondary" className="max-w-lg">
            {draftIsNoOp
              ? "Your saved draft is identical to the yaml source, so there's nothing to promote. Clear the draft to keep this prompt free of stray drafts, or edit it further on the evaluations page."
              : "You don't have any draft changes yet. Head back to the evaluations page to iterate on this prompt."}
          </Paragraph>
          <div className="flex items-center gap-4">
            <Link
              to={`/prompts/${encodeURIComponent(key)}/evaluations`}
              className="text-sm underline text-text-primary hover:text-text-secondary"
            >
              Back to evaluations
            </Link>
            {draftIsNoOp && (
              <Button
                text={deleteDraftMutation.isPending ? "Clearing…" : "Clear draft"}
                appearance="secondary"
                disabled={deleteDraftMutation.isPending}
                onClick={async () => {
                  try {
                    const resp = await deleteDraftMutation.mutateAsync({ key });
                    if (resp.status !== 200) {
                      const detail = (resp.data as { detail?: string } | undefined)?.detail;
                      notifyError(detail ?? `Failed to clear draft (status ${resp.status}).`);
                      return;
                    }
                    await queryClient.invalidateQueries({ queryKey: draftQueryKey(key) });
                    navigate(`/prompts/${encodeURIComponent(key)}/evaluations`);
                  } catch (err) {
                    notifyError(err instanceof Error ? err.message : "Failed to clear draft.");
                  }
                }}
              />
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-4">
            {changes.map((c) => (
              <DiffCard key={c.label} label={c.label} before={c.before} after={c.after} format={c.format} />
            ))}
          </div>

          <div className="fixed bottom-0 left-0 right-0 z-50 flex items-center gap-4 border-t border-border-medium bg-bg-surface px-6 py-3 shadow-lg">
            <Link
              to={`/prompts/${encodeURIComponent(key)}/evaluations`}
              className="text-sm text-text-secondary hover:text-text-primary underline"
            >
              ← Back to evaluations
            </Link>
            <span className="text-sm text-text-tertiary">
              {changes.length} field{changes.length === 1 ? "" : "s"} will change
            </span>
            <div className="flex items-center gap-3 ml-auto">
              {isNewPrompt && (
                // Target yaml file only matters for the create path —
                // the edit path finds the owning yaml automatically. On
                // an existing prompt this dropdown is skipped entirely.
                <label className="flex items-center gap-2 text-xs text-text-secondary">
                  Target yaml
                  <select
                    value={targetYamlFilename}
                    onChange={(e) => setTargetYamlFilename(e.target.value)}
                    className="rounded-md border border-border-medium bg-bg-default px-2 py-1 text-xs text-text-primary"
                  >
                    <option value="">Select…</option>
                    {yamlFilenames.map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <Button
                text={openedUrls != null ? "PR opened" : handleOpenPR.isPending ? "Opening PR…" : "Open PR"}
                appearance="primary"
                onClick={() => handleOpenPR.mutate()}
                // Once a PR has been opened for this draft view, disable
                // the button so a stray double-click can't fire a second
                // mutation and produce two identical PRs. The dialog
                // handles what happens next (view / dismiss).
                disabled={handleOpenPR.isPending || openedUrls != null || (isNewPrompt && !targetYamlFilename)}
              />
            </div>
          </div>
          {/* Bottom spacer so the fixed footer doesn't overlap the last card. */}
          <div className="h-20" />
        </>
      )}
      <PROpenedDialog
        pipelinesUrl={openedUrls?.pipelines ?? null}
        apiDjangoUrl={openedUrls?.apiDjango ?? null}
        doneLabel="Back to evaluations"
        onClose={() => {
          setOpenedUrls(null);
          // Once the user dismisses, take them back to the evaluations
          // page — the draft is now empty (queryData will refetch) and
          // there's nothing more to do on the review page.
          navigate(`/prompts/${encodeURIComponent(key ?? "")}/evaluations`);
        }}
      />
    </div>
  );
};
